import { useTranslation } from 'react-i18next';
import qrcode from 'qrcode-generator';
import { v4 as uuid } from 'uuid';
import JsBarcode from 'jsbarcode';
import getUserFullName, { parseFhirName } from '@rs-core/fhir/resource/columnMapping/utils/getUserFullName';
import { logInfo, logDebug } from '@rs-core/utils/logger';
import { useConfig } from '@rs-core/context/ConfigContext';
import { useAuth } from '@rs-core/context/UserAuthContext';
import { parseName } from '@worklist-2/core/src/fhir/resource/columnMapping/utils/getUserFullName';
import { useGlobalContext } from '../contexts/GlobalContextProvider';
import Signature from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/Signature';
import AmendmentSignature from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/AmendmentSignature';
import ProxySignature from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/ProxySignature';
import Bookmark from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/Bookmark';
import CustomField from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/CustomField';
import CustomPicklist from '../features/DocumentViewer/Viewports/ViewportTiptap/ViewportTipTapEditor/plugins/CustomPicklist';
import { BOOKMARK_OPTIONS } from '../components/drawers/constants';
import { getCurrentTime } from '../utils/getCurrentTime';
import { getCurrentDate } from '../utils/getCurrentDate';
import useGetSearchParams from '@rs-core/hooks/useGetSearchParams';
import KeyImagePlaceholder from '../assets/key-image-placeholder.png';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';
import { usePatientStore } from '@rs-ui/views/PatientInformationView/store/patientStore';
import { useOrganizationStore } from '../store/organization';
import { useBookmarksStore } from '../store/bookmarks';
import { useStudiesStore } from '../store/studies';
import { getRenamedBookmarks } from '../utils';
import { useEffect, useRef } from 'react';
import { calculatePatientAge } from '@rs-ui/views/PatientInformationView/utils/calculatePatientAge';

import ImagingStudyWorklistMapping from '@rs-core/fhir/resource/columnMapping/ImagingStudyWorklistMapping';

const useGetBookmarkValue = () => {
	const __userAuthContext = useAuth();

	const { practitionerSignature, proxyUser } = useGlobalContext();
	const { patientId: patientIdFromDV, patientInfo: patientIdFromIV } = useGetSearchParams();
	const studyShareQRLink = usePatientStore(state => state.studyShareQRLink);
	const diagnosticReportsList = useStudiesStore(state => state.diagnosticReportsList);

	const organizationTimezone = useOrganizationStore(state => state.organizationTimezone);
	const patientClass = usePatientStore(state => state.patient);
	const customFields = useBookmarksStore(state => state.customFields);
	const customPickLists = useBookmarksStore(state => state.customPickLists);
	const patient = patientClass?.patient;
	const studyShareQRLinkRef = useRef(studyShareQRLink);
	const __config = useConfig();

	const currentStudy = useStudiesStore(state => state.currentStudy);
	const { t } = useTranslation('bookmarks');
	const galaxyDvProxySign = useBooleanFlagValue('galaxy-dv-proxy-sign');
	const galaxyDvRenameInitialCursorBookmark = useBooleanFlagValue('galaxy-dv-rename-initial-cursor-bookmark');
	const crossFormatPersonName = useBooleanFlagValue('cross-format-person-name');
	let signedText = 'digitally signed by';
	if (galaxyDvProxySign && proxyUser) {
		signedText = proxyUser.extension?.find(x => x.url.includes('signedText'))?.valueString;
	}

	const isNotTemplateManager =
		window.location.pathname === '/document-viewer-v3' || window.location.pathname === '/imageviewer3d';

	const isImageViewer = window.location.pathname === '/imageviewer3d';
	const patientId = isImageViewer ? patientIdFromIV : patientIdFromDV;

	const practitionerCodes = ['performingTechnologist', 'readingPhysician'];
	const getConsultingPhysicianFullName = () => {
		const nameData = (currentStudy?.consultingPhysician || []).map(name => getUserFullName(name, true));
		return nameData;
	};
	useEffect(() => {
		logDebug('DV', 'useGetBookmarkValue: useEffect', {
			studyShareQRLinks: studyShareQRLinkRef.current,
			studyShareQRLink,
		});
		studyShareQRLinkRef.current = studyShareQRLink;
	}, [studyShareQRLink]);

	const getBookmarkValue = ({ code }) => {
		let displayData;

		if (currentStudy || patient) {
			if (code === 'patientName') {
				return parseName(currentStudy?.subject?.display, true, 'DICOM');
			}
			if (code === 'referringPhysician') {
				displayData = ImagingStudyWorklistMapping[code]?.getDisplay(currentStudy)?.referringPhysician || 'N/A';
			} else if (code === 'healthcareService') {
				displayData = patient?.[code] || ImagingStudyWorklistMapping[code]?.accessorFn?.(currentStudy) || 'N/A';
			} else if (practitionerCodes.includes(code)) {
				const practitionerName = ImagingStudyWorklistMapping[code]?.getDisplay?.(currentStudy);
				displayData = practitionerName ? getUserFullName(practitionerName, true) : 'N/A';
			} else if (code === 'patientsAge') {
				displayData = calculatePatientAge(patient?.birthDate || currentStudy?.birthDate) || 'N/A';
			} else {
				displayData =
					patient?.[code]?.display || ImagingStudyWorklistMapping[code]?.getDisplay?.(currentStudy) || 'N/A';
			}
		}

		if (code === 'QRcode') {
			displayData = t('Study Share QR Code');
		}

		if (code === 'BlumeQRcode') {
			displayData = t('Blume Registration Page QR Code');
		}

		if (code === 'AmendmentSignature') {
			displayData = t('Amendment Signature');
		}

		if (code === 'patientIdBarcode') {
			displayData = t('Patient ID Barcode');
		}

		if (typeof displayData === 'object' && code === 'referringPhysician') {
			displayData = displayData?.referringPhysician || 'N/A';
		}

		if (code === 'studyStatus') {
			displayData = displayData?.status || 'N/A';
		}
		if (code === 'referringPhysicianFaxNumber') {
			const diagonasticReportsData = diagnosticReportsList;
			const faxDetails =
				diagonasticReportsData &&
				diagonasticReportsData.length > 0 &&
				diagonasticReportsData[0]?.extension?.find(item => item.url.includes('/ReferringPhysicianFax'))
					?.valueString;
			const faxData = faxDetails && faxDetails.split(',').map(Number);
			displayData = !faxData ? 'N/A' : faxData.length === 1 ? faxData[0] : faxData.join(', ');
		}

		if (code === 'consultingPhysician') {
			const nameData = getConsultingPhysicianFullName();
			displayData = nameData.length === 0 ? 'N/A' : nameData.length === 1 ? nameData[0] : nameData.join(' | ');
		}
		if (code === 'studyShareURL') {
			logDebug('DV', 'useGetBookmarkValue: getBookmarkValue', { studyShareQRLink: studyShareQRLinkRef.current });
			displayData = studyShareQRLinkRef.current || 'N/A';
		}
		return displayData;
	};

	const getMedicalCredentials = () => {
		const nameData = __userAuthContext?.loggedInUser?.rawData?.name?.[0];
		const prefix = nameData?.prefix?.[0] ? `${nameData.prefix[0]}.` : '';
		const suffix = nameData?.suffix?.[0] ? `, ${nameData.suffix[0]}` : '';
		const fullName = __userAuthContext?.loggedInUser?.fullName || '';
		return crossFormatPersonName ? `${parseFhirName(nameData)}` : `${prefix} ${fullName}${suffix}`;
	};

	const getBookmarkHTML = ({ code, replaceSignature = false, isProxySign = false, isTranscriber = false }) => {
		const renamedBookmark = getRenamedBookmarks(BOOKMARK_OPTIONS);
		const BOOKMARKS_LIST = galaxyDvRenameInitialCursorBookmark ? renamedBookmark : BOOKMARK_OPTIONS;
		const bookmark = BOOKMARKS_LIST.find(item => item.code === code);
		if (!bookmark) {
			return;
		}

		let dataValue;

		const typeNumber = 0;
		const errorCorrectionLevel = 'Q';

		if (isNotTemplateManager) {
			const signedLabel = t('digitally signed by').toUpperCase();
			const transcribedByLabel = t('Transcribed by').toUpperCase();
			const timeLabel = t('time');
			const dateLabel = t('date');
			const amendedLabel = t('amended by').toUpperCase();
			const medicalCredentials = getMedicalCredentials().toUpperCase();

			if (code === 'signature' && replaceSignature) {
				logInfo('SIGNATURE::', `replaceSignature::`, replaceSignature);
				// If Proxy sign is clicked
				if (isProxySign) {
					logInfo('SIGNATURE::proxy', `proxyUser::`, proxyUser);
					const signedTextLabel = t(signedText).toUpperCase();
					return `<div data-type="${ProxySignature.name}"
					data-signed-text-label="${signedTextLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}"
					data-image="${
						proxyUser?.superUserSignature
					}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
						organizationTimezone
					)}" data-time="${getCurrentTime(organizationTimezone)}" data-signed-text="${signedText}"></div>`;
				}

				// If Sign Clicked on Transcriber Flow
				if (isTranscriber) {
					logInfo('SIGNATURE::isTranscriber', `practitionerSignature::`, practitionerSignature);
					return `<div data-type="${
						Signature.name
					}" data-signed-label="${transcribedByLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}" data-image="${practitionerSignature}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
						organizationTimezone
					)}" data-time="${getCurrentTime(organizationTimezone)}"></div>`;
				}
				logInfo('SIGNATURE::prelim', `practitionerSignature::`, practitionerSignature);
				// If Sign study is clicked
				return `<span data-type="${
					Signature.name
				}" data-signed-label="${signedLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}" data-image="${practitionerSignature}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
					organizationTimezone
				)}" data-time="${getCurrentTime(organizationTimezone)}"
        		></span>`;
			}

			if (code === 'signature' && !replaceSignature) {
				return `<span data-preview="true" data-type="${
					Signature.name
				}" data-signed-label="${signedLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}" data-image="${practitionerSignature}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
					organizationTimezone
				)}" data-time="${getCurrentTime(organizationTimezone)}"
				></span>`;
			}

			if (code === 'AmendmentSignature' && replaceSignature) {
				// If Amending user is proxy
				logInfo('SIGNATURE::AmendmentSignature', `replaceSignature::`, replaceSignature);
				if (isProxySign) {
					logInfo('SIGNATURE::AmendmentSignature', `isProxySign::`, proxyUser);
					const signedTextLabel = t(signedText);
					return `<div data-type="${AmendmentSignature.name}"
					data-amended-label="${amendedLabel}"
					data-signed-text-label="${signedTextLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}"
					data-image="${
						proxyUser?.superUserSignature
					}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
						organizationTimezone
					)}" data-time="${getCurrentTime(organizationTimezone)}"></div>`;
				}
				logInfo('SIGNATURE::AmendmentSignature', `amendment::`, practitionerSignature);
				return `<span data-type="${
					AmendmentSignature.name
				}" data-amended-label="${amendedLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}" data-image="${practitionerSignature}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
					organizationTimezone
				)}" data-time="${getCurrentTime(organizationTimezone)}"
				></span>`;
			}

			if (code === 'AmendmentSignature' && !replaceSignature) {
				return `<span data-preview="true" data-type="${
					AmendmentSignature.name
				}" data-amended-label="${amendedLabel}" data-date-label="${dateLabel}" data-time-label="${timeLabel}" data-image="${practitionerSignature}" data-medical-credentials="${medicalCredentials}" data-date="${getCurrentDate(
					organizationTimezone
				)}" data-time="${getCurrentTime(organizationTimezone)}"
				></span>`;
			}

			if (code === 'QRcode') {
				const qr = qrcode(typeNumber, errorCorrectionLevel);
				const link = studyShareQRLinkRef.current || __config.omegaai_url;
				qr.addData(link);
				qr.make();
				const innerHTML = qr.createImgTag(4);
				const innerHTMLWithTypeAttr = innerHTML.replace(
					/<img(.*?)(\/)?>/,
					`<a href=${link} target="_blank" rel="noopener noreferrer"><img$1 data-type="qr-code"$2></a>`
				);
				return `<div data-type="${Bookmark.name}">${innerHTMLWithTypeAttr}</div>`;
			}

			if (code === 'BlumeQRcode') {
				const qr = qrcode(typeNumber, errorCorrectionLevel);
				qr.addData(__config.blume_base_url);
				qr.make();
				const innerHTML = qr.createImgTag(4);
				const innerHTMLWithTypeAttr = innerHTML.replace(/<img(.*?)(\/)?>/, '<img$1 data-type="qr-code"$2>');
				return `<div data-type="${Bookmark.name}">${innerHTMLWithTypeAttr}</div>`;
			}

			if (code === 'initCursorPos') {
				return `<span ${dataValue && `data-value=""`} data-type="${
					Bookmark.name
				}" data-code="${code}" data-display="" data-hidden="true" data-exd-conc="true"></span>`;
			}

			dataValue = code !== 'signature' ? getBookmarkValue({ code }) : 'signature';

			if (code === 'patientIdBarcode') {
				const img = document.createElement('img');
				img.width = 250;
				img.height = 90;
				JsBarcode(img, patientId, {
					font: 'Roboto',
					text: patientId,
				});
				return img.outerHTML;
			}

			if (code === 'studyShareURL') {
				logDebug('DV', 'useGetBookmarkValue: getBookmarkHTML', {
					studyShareQRLink: studyShareQRLinkRef.current,
				});
				return `<a data-type="${Bookmark.name}" href=${studyShareQRLinkRef.current} target="_blank" rel="noopener noreferrer">${studyShareQRLinkRef.current}</a>`;
			}
		}
		if (code === 'keyImages') {
			//src and key-index is empty will be setted for report only
			return `<img data-code="${code}"  id="key-image" src=${KeyImagePlaceholder} width="${200}" height="${200}" data-uid="${uuid()}" key-index=""/>`;
		}

		return `<span ${dataValue ? `data-value="${dataValue}"` : ''} data-type="${
			Bookmark.name
		}" data-code="${code}" data-display="${t(bookmark.display)}"></span>`;
	};

	const getCustomFieldHTML = ({ code }, metaData) => {
		const field = customFields?.find(item => item.code === code) || metaData;

		if (!field) return;
		const { fieldId, isBlank } = field;
		// Conditionally include the data-fieldId attribute only if fieldId is defined
		const fieldIdAttribute = fieldId !== undefined ? ` data-fieldId="${fieldId}"` : '';

		// Conditionally include the data-isBlank attribute only if isBlank is defined
		const isBlankAttribute = isBlank !== undefined ? ` data-isBlank="${isBlank}"` : '';

		// Conditionally include the contentId attribute only if fieldId is defined
		const contentIdAttribute = ` contentId="${fieldId !== undefined ? fieldId : 'id-001'}"`;

		return `<div data-type="${
			CustomField.name
		}" data-hide="${false}" data-code="${code}" data-uid="${uuid()}" data-display="${t(
			field.display
		)}"${fieldIdAttribute}${isBlankAttribute}><div${contentIdAttribute}><p></p></div></div>`;
	};

	const getCustomFieldData = ({ fieldid }) => {
		const field = customFields?.find(item => item.fieldid === +fieldid);

		if (!field) return;

		return field;
	};

	const getCustomPicklistHTML = ({ code }, metaData) => {
		const picklist = customPickLists?.find(item => item.code === code) || metaData;

		if (!picklist) return;

		const defaultValue = picklist?.options?.find(x => x.isDefault === true);

		if (isNotTemplateManager) {
			return `<span data-type="${
				CustomPicklist.name
			}" data-hide="${false}" data-code="${code}" data-uid="${uuid()}" data-display="${t(
				picklist.display
			)}" data-fieldId="${picklist.fieldId}" data-value="${defaultValue?.item}"></span>`;
		}

		return `<span data-type="${CustomPicklist.name}" data-hide="${true}" data-code="${code}" data-display="${t(
			picklist.display
		)}" data-fieldId="${picklist.fieldId}" data-value="${defaultValue?.item}"></span>`;
	};

	const getCustomPicklistData = ({ fieldid }) => {
		const picklist = customPickLists?.find(item => item.fieldId === +fieldid);

		if (!picklist) return;

		const defaultValue = picklist?.options?.find(x => x.isDefault === true);

		return {
			defaultValue,
			...picklist,
		};
	};

	return {
		getBookmarkValue,
		getBookmarkHTML,
		getCustomFieldHTML,
		getCustomPicklistHTML,
		getCustomPicklistData,
		getCustomFieldData,
	};
};

export default useGetBookmarkValue;
